<template>
  <v-container class="sweetspot-saved px-6 pt-0 pb-3">
    <v-dialog v-model="dialogCreate" width="1200px">
      <SweetSpotCreate
        :parent_id="curr_folder_id"
        v-on:report_published="report_published"
      />
    </v-dialog>
    <v-row class="sweetspot-saved__content ma-0 pa-0">
      <v-row
        class="sweetspot-saved__loading ma-0 pa-0"
        align="center"
        justify="center"
        v-if="page_loading && sweetspot_reports.length === 0"
      >
        <v-col cols="auto">
          <v-progress-circular
            color="black"
            size="100"
            indeterminate
          ></v-progress-circular>
        </v-col>
      </v-row>
      <v-col class="ma-0 pa-0" cols="12" v-else>
        <EntityDirectory
          entity_name="Plan"
          :directory_data="sweetspot_reports"
          :loading="directory_loading"
          :available_create_options="['create_folder', 'create_file']"
          :item_subcontent_config="item_subcontent_config"
          v-on:open_entity="open_report"
          v-on:edit_entity_name="update_report"
          v-on:delete_entity="delete_report"
          v-on:move_files="move_reports"
          v-on:create_entity_folder="create_report_folder"
          v-on:create_entity="create_report"
          v-on:reset_directory="get_sweetspot_reports"
        />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import sweetspot_reporting_api_caller from "@/javascript/sweetspot_reporting.js";
import EntityDirectory from "@/components/Utility/EntityDirectory.vue";
import SweetSpotCreate from "./SweetSpotComponents/SweetSpotCreate.vue";
import {
  ALERT_TYPE_ERROR,
  ALERT_TYPE_SUCCESS,
  ALERT_GENERIC_MESSAGE,
} from "@/constants/constants";

export default {
  name: "SweetSpotSaved",
  components: {
    EntityDirectory,
    SweetSpotCreate,
  },
  props: {
    report_loading: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    client_id: "0000",
    page_loading: true,
    saved_report_loading: true,
    dialogCreate: false,
    curr_folder_id: null,
    item_subcontent_config: [],
    sweetspot_reports: [],
  }),
  computed: {
    directory_loading() {
      return this.saved_report_loading || this.report_loading;
    },
  },

  async mounted() {
    await this.get_sweetspot_reports();

    this.page_loading = false;
  },
  methods: {
    findReportById(items, reportId) {
      if (!Array.isArray(items)) {
        return null;
      }

      for (const item of items) {
        if (item.id === reportId && !item.is_directory) {
          return item;
        }

        const nestedMatch = this.findReportById(item.children, reportId);
        if (nestedMatch) {
          return nestedMatch;
        }
      }

      return null;
    },
    get_sweetspot_reports: async function () {
      this.saved_report_loading = true;

      let payload = {
        project_id: this.$project.get_val(),
      };

      let token = await this.$auth.getTokenSilently();

      let result = await sweetspot_reporting_api_caller
        .get_reports(token, payload)
        .then((resp) => {
          if (resp["success"]) {
            return resp["data"];
          }
          return false;
        })
        .catch(() => {
          return false;
        });
      if (result && result["sweetspot_reports"]) {
        this.sweetspot_reports = result["sweetspot_reports"];
      } else {
        this.$emit("triggerAlert", ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
      }

      this.saved_report_loading = false;
    },
    create_report_folder: async function (payload) {
      let create_payload = {
        project_id: this.$project.get_val(),
        parent_id: payload.parent_id,
        folder_name: payload.folder_name,
      };

      let token = await this.$auth.getTokenSilently();
      let result = await sweetspot_reporting_api_caller
        .create_report_folder(token, create_payload)
        .then((resp) => {
          if (resp["success"]) {
            return resp["data"];
          }
          return false;
        })
        .catch(() => {
          return false;
        });
      if (result) {
        await this.get_sweetspot_reports();
      } else {
        this.$emit("triggerAlert", ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
      }
    },
    create_report: function (parent_id) {
      this.curr_folder_id = parent_id;
      this.dialogCreate = true;
    },
    getCreatedReportId(payload) {
      if (!payload || typeof payload !== "object") {
        return null;
      }

      return (
        payload.report_id ||
        payload.id ||
        payload.new_report_id ||
        payload.report?.report_id ||
        payload.report?.id ||
        payload.new_report?.report_id ||
        payload.new_report?.id ||
        null
      );
    },
    report_published: async function (payload) {
      this.dialogCreate = false;
      this.curr_folder_id = null;

      if (payload) {
        const createdReportId = this.getCreatedReportId(payload);

        await this.get_sweetspot_reports();

        if (createdReportId) {
          this.open_report(createdReportId);
        }
      } else {
        this.$emit("triggerAlert", ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
      }
    },
    move_reports: async function (payload) {
      const reportIds = (payload.entity_ids || []).map((id) => Number(id));

      if (
        reportIds.length === 0 ||
        payload.parent_id === null ||
        payload.parent_id === undefined
      ) {
        this.$emit("triggerAlert", ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
        return;
      }

      this.saved_report_loading = true;
      const movePayload = {
        project_id: this.$project.get_val(),
        report_ids: reportIds,
        parent_id: payload.parent_id,
      };

      let token = await this.$auth.getTokenSilently();
      let result = await sweetspot_reporting_api_caller
        .move_reports(token, movePayload)
        .then((resp) => {
          if (resp["success"]) {
            return resp["data"];
          }
          return false;
        })
        .catch(() => {
          return false;
        });

      if (result) {
        await this.get_sweetspot_reports();
      } else {
        this.$emit("triggerAlert", ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
        this.saved_report_loading = false;
      }
    },

    update_report: async function (payload) {
      let report_id = payload.id;
      let report_name = payload.name;

      if (report_id) {
        let update_payload = {
          client_id: this.client_id,
          report_id: report_id,
          update_vals: {
            report_name: report_name,
          },
        };

        let token = await this.$auth.getTokenSilently();
        let result = await sweetspot_reporting_api_caller
          .update_report(token, update_payload)
          .then((resp) => {
            if (resp["success"]) {
              return resp["data"];
            }
            return false;
          })
          .catch(() => {
            return false;
          });

        if (!result || !result["report_id"]) {
          this.$emit("triggerAlert", ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
        } else {
          this.$emit(
            "triggerAlert",
            ALERT_TYPE_SUCCESS,
            "Report name updated successfully"
          );
        }
      }
      await this.get_sweetspot_reports();
    },
    open_report: function (report_id) {
      if (this.directory_loading) {
        return;
      }

      const reportItem = this.findReportById(this.sweetspot_reports, report_id);

      this.$emit("open_report", {
        report_id,
        updated_time:
          (reportItem && (reportItem.updated_time || reportItem.updated_at || reportItem.last_updated)) ||
          null,
      });
    },
    delete_report: async function (report_id) {
      this.saved_report_loading = true;
      let payload = {
        client_id: this.$project.get_val().slice(0, 4),
        report_id: report_id,
      };

      let token = await this.$auth.getTokenSilently();
      let result = await sweetspot_reporting_api_caller
        .delete_report(token, payload)
        .then((resp) => {
          if (resp["success"]) {
            return resp["data"];
          }
          return false;
        })
        .catch(() => {
          return false;
        });

      if (!result || !result["report_id"]) {
        this.$emit("triggerAlert", ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
      }
      await this.get_sweetspot_reports();
    },
  },
};
</script>

<style scoped>
.sweetspot-saved {
  max-width: 100%;
  height: 100%;
  font-family: "Poppins";
}

.sweetspot-saved__content,
.sweetspot-saved__loading {
  width: 100%;
  height: 100%;
}
</style>
